'use client';
import React from 'react';
import { useLocation, Outlet } from 'react-router';
import { useResizableSidebar } from '../hooks/useResizableSidebar';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import { useLayoutVisibility } from '../hooks/useLayoutVisibility';
import { createLayoutStyles } from '../utils/layoutStyles';
import { ConditionalSidebar, ConditionalRightSidebar, ConditionalMobileNavigation, ConditionalMainContent } from './ConditionalLayoutAreas';
/**
 * 최대한 간소화된 MainLayout - 책임 완전 분산
 * 조건부 렌더링 로직을 완전히 분리하여 Single Responsibility 구현
 */ const MainLayout = ({ variant = 'standard', children, useReactRouterOutlet = false, injectedLocation, className, themeProvider, showRightSidebar = false, showChatSidebar = false, hideNavigation = false, renderSidebar, renderHeader, headerLeft, headerActions, showHeader, renderRightSidebar, renderMobileNavigation, chatOptions })=>{
    // 🚀 DEPENDENCY INJECTION: Router context (safe fallback)
    let reactRouterLocation;
    try {
        reactRouterLocation = useLocation();
    } catch (error) {
        // Router context가 없는 경우 (예: Excel Viewer 등)
        reactRouterLocation = {
            pathname: '/'
        };
    }
    const location = injectedLocation || reactRouterLocation;
    // 🎯 각 책임별로 완전히 분리된 Hooks
    const responsive = useResponsiveLayout(themeProvider?.breakpoints);
    const resizable = useResizableSidebar(chatOptions);
    const visibility = useLayoutVisibility({
        variant,
        hideNavigation,
        showRightSidebar,
        showChatSidebar,
        renderSidebar,
        renderRightSidebar,
        renderMobileNavigation,
        ...responsive
    });
    // 🎨 SINGLE RESPONSIBILITY: 스타일 로직만 담당
    const styles = createLayoutStyles({
        hasRightSidebar: visibility.rightSidebar,
        rightSidebarWidth: resizable.width,
        enableResize: chatOptions?.enableResize || false,
        isMobileView: responsive.isMobileView,
        isTabletView: responsive.isTabletView,
        viewportWidth: responsive.screenWidth
    });
    return /*#__PURE__*/ React.createElement("div", {
        className: `main-layout main-layout--${variant} ${className || ''}`,
        style: styles.containerStyle
    }, /*#__PURE__*/ React.createElement(ConditionalSidebar, {
        show: visibility.sidebar,
        render: renderSidebar,
        style: styles.sidebarStyle
    }), /*#__PURE__*/ React.createElement(ConditionalMainContent, {
        renderHeader: renderHeader,
        headerLeft: headerLeft,
        headerActions: headerActions,
        showHeader: showHeader,
        style: styles.mainStyle
    }, useReactRouterOutlet ? /*#__PURE__*/ React.createElement(Outlet, {
        key: location.pathname
    }) : children), /*#__PURE__*/ React.createElement(ConditionalRightSidebar, {
        show: visibility.rightSidebar,
        render: renderRightSidebar,
        style: styles.rightSidebarStyle,
        isResizing: resizable.isResizing,
        onMouseDown: resizable.handleResizeStart,
        resizeHandleProps: resizable.resizeHandleProps
    }), /*#__PURE__*/ React.createElement(ConditionalMobileNavigation, {
        show: visibility.mobileNavigation,
        render: renderMobileNavigation,
        style: styles.mobileNavStyle
    }));
};
export default MainLayout;
